import React, { useState, useContext } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { FilterContext } from '../../context/FilterContext';
import { activitiesAPI } from '../../services/api';
import { trackEvent, getCurrentActivityEventType } from '../../services/analytics';
import { User, Manual, Delete, Archieve, ArrowL, PDF } from '../Icons/CustomIcons';
import { DoCancelDialog, GoToDialog, PdfLoadingDialog } from './Dialog';
import { RoundButtonS } from './Button';

const Header = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated } = useAuth();
  const { filters } = useContext(FilterContext) || {};

  const isActivityDetail = /^\/activities\//.test(location.pathname);
  const isActivityControl = /^\/aktivitaet-kontrolle\//.test(location.pathname);
  const hasBackNav = isActivityDetail || isActivityControl || location.pathname === '/meine-aktivitaeten';
  const activityControlId = isActivityControl
    ? location.pathname.match(/^\/aktivitaet-kontrolle\/(\d+)/)?.[1]
    : null;
  const activityDetailId = isActivityDetail
    ? location.pathname.match(/^\/activities\/(\d+)/)?.[1]
    : null;

  const backTo = isActivityControl ? '/meine-aktivitaeten' : '/';
  const backLabel = isActivityControl ? 'Zurück zu meinen Aktivitäten' : 'Zurück zur Startseite';

  // Logo führt überall zu "Über uns" – nur auf dieser Seite selbst zur Startseite
  const isUeberUns = location.pathname === '/ueber-uns';
  const logoTo = isUeberUns ? '/' : '/ueber-uns';
  const logoLabel = isUeberUns ? 'Zur Startseite' : 'Zu «Über uns»';

  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteSuccess, setDeleteSuccess] = useState(false);

  const [showArchiveConfirm, setShowArchiveConfirm] = useState(false);
  const [archiveLoading, setArchiveLoading] = useState(false);
  const [archiveSuccess, setArchiveSuccess] = useState(false);

  const [pdfState, setPdfState] = useState('idle'); // 'idle' | 'loading' | 'ready' | 'error'
  const [pdfBlobUrl, setPdfBlobUrl] = useState(null);

  const handlePdfClick = async () => {
    if (!activityDetailId || pdfState === 'loading') return;
    // Ein Button für beide Varianten: unter 600px blendet header.css das Label aus
    trackEvent('pdf-export', {
      id: Number(activityDetailId),
      event_type: getCurrentActivityEventType(activityDetailId),
      variant: window.matchMedia('(min-width: 600px)').matches ? 'desktop' : 'mobile',
    });
    if (pdfBlobUrl) URL.revokeObjectURL(pdfBlobUrl);
    setPdfBlobUrl(null);
    setPdfState('loading');
    try {
      const base = activitiesAPI.getPdfUrl(activityDetailId);
      const params = new URLSearchParams();
      if (filters?.start_address) params.set('from_address', filters.start_address);
      if (filters?.departure_date) params.set('departure_date', filters.departure_date);
      if (filters?.departure_time) params.set('departure_time', filters.departure_time);
      const qs = params.toString();
      const response = await fetch(qs ? `${base}?${qs}` : base, { credentials: 'include' });
      if (!response.ok) throw new Error('PDF-Generierung fehlgeschlagen');
      const blob = await response.blob();
      setPdfBlobUrl(URL.createObjectURL(blob));
      setPdfState('ready');
    } catch {
      setPdfState('error');
    }
  };

  const handleClosePdfModal = () => {
    if (pdfBlobUrl) URL.revokeObjectURL(pdfBlobUrl);
    setPdfBlobUrl(null);
    setPdfState('idle');
  };

  const handleDownloadPdf = () => {
    if (!pdfBlobUrl) return;
    const a = document.createElement('a');
    a.href = pdfBlobUrl;
    a.download = 'aktivitaet.pdf';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const handleDelete = async () => {
    setDeleteLoading(true);
    try {
      await activitiesAPI.delete(activityControlId);
      setShowDeleteConfirm(false);
      setDeleteSuccess(true);
      navigate('/meine-aktivitaeten');
    } catch (err) {
      console.error('Fehler beim Löschen:', err);
      setDeleteLoading(false);
      setShowDeleteConfirm(false);
    }
  };

  const handleArchive = async () => {
    setArchiveLoading(true);
    try {
      await activitiesAPI.archive(activityControlId);
      setShowArchiveConfirm(false);
      setArchiveSuccess(true);
      navigate('/meine-aktivitaeten');
    } catch (err) {
      console.error('Fehler beim Archivieren:', err);
      setArchiveLoading(false);
      setShowArchiveConfirm(false);
    }
  };

  const handleLoginClick = () => {
    const returnTo = encodeURIComponent(location.pathname + location.search);
    navigate(`/anmelden?von=${returnTo}`);
  };

  return (
    <>
      <header className="header">
        {/* ── Links: Logo + Back-Button ─────────────────────────────────────── */}
        <div className="header-left">
          {/* Auf der Aktivitaets-Detailseite bleibt das Logo auch unter 1024px stehen,
              damit der Header dort gleich hoch ist wie auf der Startseite. */}
          {hasBackNav && (
            <Link to={backTo} className="header-back-btn" aria-label={backLabel} title={backLabel}>
              <ArrowL size={20} />
              <span className="header-back-label">{backLabel}</span>
            </Link>
          )}
          <Link
            to={logoTo}
            className={`header-logo${hasBackNav && !isActivityDetail ? ' header-logo--hidden-mobile' : ''}`}
            aria-label={logoLabel}
          >
            <img
              src="/logo-mobile.svg"
              className="header-logo-img header-logo-img--mobile"
              alt="grosskinder-aktiv.ch"
            />
            <img
              src="/logo-desktop.svg"
              className="header-logo-img header-logo-img--desktop"
              alt="grosskinder-aktiv.ch"
            />
          </Link>

        </div>

        {/* ── Rechts: Aktions-Buttons + Sprache + User/Login ───────────────── */}
        <div className="header-right">
          {isActivityControl && activityControlId && (
            <>
              <RoundButtonS
                className="btn-primary"
                onClick={() => navigate(`/aktivitaet-kontrolle/${activityControlId}?bearbeiten=1`)}
                title="Aktivität bearbeiten"
              >
                <Manual size={24} color="var(--Colors-White, #fff)" />
              </RoundButtonS>
              <RoundButtonS
                className="icon-btn--secondary"
                onClick={() => setShowDeleteConfirm(true)}
                title="Aktivität löschen"
              >
                <Delete size={24} color="var(--Colors-Accent-01, #006475)"/>
              </RoundButtonS>
              <RoundButtonS
                className="icon-btn--secondary"
                onClick={() => setShowArchiveConfirm(true)}
                title="Aktivität archivieren"
              >
                <Archieve size={24} color="var(--Colors-Accent-01, #006475)"/>
              </RoundButtonS>

              <span className="header-divider" aria-hidden="true" />
            </>
          )}

          {isActivityDetail && (
            <button
              type="button"
              onClick={handlePdfClick}
              disabled={pdfState === 'loading'}
              className="btn btn-primary header-pdf-btn"
              title="Aktivität als PDF herunterladen"
            >
              <PDF size={20} />
              <span className="header-btn-label">Als PDF ansehen</span>
            </button>
          )}

          {!isActivityDetail && (
            isAuthenticated ? (
              <RoundButtonS
                className="btn-terziaery"
                onClick={() => navigate('/meine-aktivitaeten')}
                title="Mein Profil"
            >
              <User size={20}/>
            </RoundButtonS>
          ) : (
            <button
              type="button"
              className="btn user-button-login"
              onClick={handleLoginClick}
            >
              Log In
            </button>
          ))}
        </div>
      </header>

      {showDeleteConfirm && (
        <DoCancelDialog
          icon={Delete}
          title="Aktivität wirklich löschen?"
          text={deleteLoading ? 'Wird gelöscht…' : 'Endgültig löschen'}
          message="Die Aktivität wird dauerhaft gelöscht und kann nicht wiederhergestellt werden."
          onConfirm={handleDelete}
          onCancel={() => setShowDeleteConfirm(false)}
        />
      )}

      {deleteSuccess && (
        <GoToDialog
          icon={Delete}
          title="Aktivität gelöscht."
          text="Zum Profil"
          message="Sie werden zu ihrem Profil weitergeleitet."
          onConfirm={() => setDeleteSuccess(false)}
        />
      )}

      {showArchiveConfirm && (
        <DoCancelDialog
          icon={Archieve}
          title="Aktivität archivieren?"
          text={archiveLoading ? 'Wird archiviert…' : 'Archivieren'}
          message="Die Aktivität wird archiviert und ist nicht mehr öffentlich sichtbar."
          onConfirm={handleArchive}
          onCancel={() => setShowArchiveConfirm(false)}
        />
      )}

      {archiveSuccess && (
        <GoToDialog
          icon={Archieve}
          title="Aktivität archiviert."
          text="Zum Profil"
          message="Sie werden zu ihrem Profil weitergeleitet."
          onConfirm={() => setArchiveSuccess(false)}
        />
      )}

      {pdfState !== 'idle' && (
        <PdfLoadingDialog
          state={pdfState}
          onDownload={handleDownloadPdf}
          onClose={handleClosePdfModal}
        />
      )}

    </>
  );
};

export default Header;
